import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import useRequest from '@/hooks/useRequest';
import { MISSION_API_VERSION_HEADER } from '@/pages/missions/utils';

export default function useIsReadyToRelease() {
  const { appId, projectId } = useParams<AppParam>();
  const { data: projectData } = useRequest<CommonResponseModel<ProjectV2>>(
    `/apps/${appId}/projects/${projectId}`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const project = projectData?.data;
  const { data: played } = useRequest<Played>(`/api/statistics/projects/${projectId}/played`);

  const { quests, missions } = useQuests();

  const hasFullScenario = useMemo(() => {
    if (!quests) return undefined;

    const flatten = {
      reactionCommands: quests
        .flatMap((quest) =>
          Array.isArray(quest.actionHandler?.reactionDefinitions)
            ? quest.actionHandler.reactionDefinitions
            : []
        )
        .flatMap(({ commands }) => commands),
      failureCommands: quests.flatMap((quest) =>
        Array.isArray(quest.actionHandler?.failureCommands) ? quest.actionHandler.failureCommands : []
      )
    };

    const merged = [...flatten.reactionCommands, ...flatten.failureCommands];

    const hasStartScenario =
      merged.findIndex(({ type }) => type === 'RealWorld.Commands.StartScenario') !== -1;

    const hasAccomplishScenario =
      merged.findIndex(
        ({ type }) =>
          type === 'RealWorld.Commands.AccomplishScenario' ||
          type === 'RealWorld.Commands.AccomplishScenario2'
      ) !== -1;

    return hasStartScenario && hasAccomplishScenario;
  }, [quests]);

  const hasVisibleMission =
    missions && missions.length > 0
      ? missions?.some((mission) => mission.isVisibleDefault)
      : undefined;

  const isPlayed = played?.isStartedProject && played.isFinishedProject;

  return {
    isRequiredInfosFilled: project && isRequiredInfosFilled(project),
    hasFullScenario,
    hasVisibleMission,
    isPlayed,
    project
  };
}

function isRequiredInfosFilled(project: ProjectV2) {
  const commonRequireFields = [
    project.name,
    project.projectType,
    project.playTimeInMinutes,
    project.difficulty !== 0,
    project.storySummary
  ];

  if (commonRequireFields.filter(Boolean).length !== commonRequireFields.length) return false;

  if (passByCommonRequireField.includes(project.projectType)) return true;

  if (requirePlayAreaGameType.includes(project.projectType)) return !!project.offlineInfo;

  throw Error('기대하지 않은 경우입니다.');
}

const passByCommonRequireField: Project['projectGameType'][] = ['Online', 'OnlineKit', 'Etc'];
const requirePlayAreaGameType: Project['projectGameType'][] = ['Offline', 'OfflineKit'];

function useQuests() {
  const { appId, projectId } = useParams<AppParam>();

  const { data: scenariosPayload } = useRequest<unknown>(
    `/apps/${appId}/projects/${projectId}/scenarios`
  );
  const scenarios = extractScenarios(scenariosPayload);

  const scenarioId = scenarios && scenarios.length > 0 ? scenarios[0].id : undefined;

  const { data: missionsPayload } = useRequest<unknown>(
    typeof scenarioId === 'number' ? `/apps/${appId}/scenarios/${scenarioId}/missions` : null,
    {},
    MISSION_API_VERSION_HEADER
  );
  const missions = extractMissions(missionsPayload);

  const quests =
    typeof scenarioId === 'number'
      ? missions?.flatMap((mission) => (Array.isArray(mission.quests) ? mission.quests : []))
      : undefined;

  return { quests, missions };
}

function extractScenarios(payload: unknown): Scenario[] | undefined {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const response = payload as Record<string, unknown>;

  if (Array.isArray(response.scenarios)) {
    return response.scenarios as Scenario[];
  }

  if (Array.isArray(response.data)) {
    return response.data as Scenario[];
  }

  return undefined;
}

function extractMissions(payload: unknown): Mission[] | undefined {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const response = payload as Record<string, unknown>;

  if (Array.isArray(response.missions)) {
    return response.missions as Mission[];
  }

  if (Array.isArray(response.data)) {
    return response.data as Mission[];
  }

  if (response.data && typeof response.data === 'object') {
    const nestedData = response.data as Record<string, unknown>;
    if (Array.isArray(nestedData.missions)) {
      return nestedData.missions as Mission[];
    }
  }

  return undefined;
}
